import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spinner, Text, Button, makeStyles, tokens } from '@fluentui/react-components';
import Breadcrumb from '../Breadcrumb';
import DetailView from '../DetailView';
import { fetchIssues } from '../../services/api';
import { Issue, BreadcrumbItem } from '../../types';

interface RootCauseDetailViewProps {
  apiUrl?: string;
  onNavigate?: (path: string) => void;
}

const useStyles = makeStyles({
  container: {
    maxWidth: '80rem',
    margin: '0 auto',
    padding: tokens.spacingVerticalXXL,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '80vh',
    gap: tokens.spacingVerticalM,
  },
  notFoundContainer: {
    textAlign: 'center',
    padding: tokens.spacingVerticalXXL,
  },
});

const RootCauseDetailView: React.FC<RootCauseDetailViewProps> = ({ apiUrl, onNavigate }) => {
  const { domain, category, subcategory, issue, path } = useParams<{ 
    domain: string; 
    category: string; 
    subcategory: string;
    issue: string;
    path: string;
  }>();
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const styles = useStyles();

  useEffect(() => {
    const loadIssues = async (): Promise<void> => {
      try {
        const data = await fetchIssues();
        const foundIssue = data.find((i) => i.Path === decodeURIComponent(path || ''));
        setSelectedIssue(foundIssue || null);
        setLoading(false);
      } catch (error) {
        console.error('Failed to load issues:', error);
        setLoading(false);
      }
    };
    if (path) {
      loadIssues();
    }
  }, [path, apiUrl]);

  const handleBack = (): void => {
    if (!domain || !category || !subcategory || !issue) return;
    const backPath = `/${encodeURIComponent(domain)}/${encodeURIComponent(category)}/${encodeURIComponent(subcategory)}/${encodeURIComponent(issue)}`;
    if (onNavigate) {
      onNavigate(backPath);
    } else {
      navigate(backPath);
    }
  };

  const breadcrumbPath: BreadcrumbItem[] =
    domain && category && subcategory && issue
      ? [
          { label: decodeURIComponent(domain), value: domain },
          { label: decodeURIComponent(category), value: category },
          { label: decodeURIComponent(subcategory), value: subcategory },
          { label: decodeURIComponent(issue), value: issue },
          {
            label: selectedIssue?.Taxonomy.RootCause || 'Details',
            value: path || '',
          },
        ]
      : [];

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" />
        <Text>Loading issue details...</Text>
      </div>
    );
  }

  if (!selectedIssue) {
    return (
      <div className={styles.container}>
        <div className={styles.notFoundContainer}>
          <Text>Issue not found</Text>
          <div style={{ marginTop: tokens.spacingVerticalM }}>
            <Button
              appearance="subtle"
              onClick={() => {
                if (onNavigate) {
                  onNavigate('/');
                } else {
                  navigate('/');
                }
              }}
            >
              Return to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Breadcrumb path={breadcrumbPath} />
      <DetailView issue={selectedIssue} onBack={handleBack} />
    </div>
  );
};

export default RootCauseDetailView;

